// src/App.jsx
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Login from "./pages/Login";
import Calendar from "./pages/Calendar";
import Customers from "./pages/Customers";
import CustomerJobs from "./pages/CustomerJobs";
import ViewCustomerRooms from "./pages/ViewCustomerRooms";
import AuthGuard from "./components/AuthGuard";
import Layout from "./components/Layout";
import Invoices from "./pages/Invoices";
import AddInvoice from "./pages/AddInvoice";
import ViewInvoice from "./pages/ViewInvoice";
import Todo from "./pages/Todo";
import Drivers from "./pages/Drivers";
import DriverQueue from "./pages/DriverQueue";

// Only import DatabaseBackup in development mode (excluded from production build)
// This ensures the component code is NOT included in the production bundle
const DatabaseBackup = import.meta.env.DEV
  ? lazy(() => import("./pages/DatabaseBackup"))
  : null;

// Get the base URL from the environment variable
const baseUrl = import.meta.env.VITE_BASE_URL;

/**
 * Main App Component
 *
 * Defines the routing structure for the P&M Admin application.
 * Features protected routes with authentication guards and shared layout.
 * Now uses clean table design consistent throughout the app.
 *
 * Route Structure:
 * - /login - Public login page
 * - / - Root redirects to calendar (default landing page)
 * - /calendar - Calendar page for managing appointments and events
 * - /customers - Customer management page with clean table design
 * - /todo - To do list page showing customers marked for follow-up
 * - /customers/:customerId/jobs - Individual customer jobs page
 * - /customers/:customerId/jobs/:jobId - Individual customer jobs page with highlighted job
 * - /customers/:id/jobs/:jobId/rooms - Customer job rooms and measurements page
 * - /invoices - Invoice list page with consistent table design
 * - /invoices/add - Add new invoice page
 * - /invoices/:id - View/edit specific invoice page
 */
function App() {
  return (
    <BrowserRouter
      basename={baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl}
    >
      <Routes>
        {/* Public Login Route */}
        <Route
          path="/login"
          element={
            <AuthGuard requireAuth={false}>
              <Login />
            </AuthGuard>
          }
        />

        {/* Protected Routes with Shared Layout */}
        <Route element={<Layout />}>
          {/* Root Route - Redirect to Calendar */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Calendar />
              </AuthGuard>
            }
          />

          {/* Calendar Route - Main landing page */}
          <Route
            path="/calendar"
            element={
              <AuthGuard>
                <Calendar />
              </AuthGuard>
            }
          />

          {/* Customer Management Routes */}
          <Route
            path="/customers"
            element={
              <AuthGuard>
                <Customers />
              </AuthGuard>
            }
          />

          {/* To Do List Route */}
          <Route
            path="/todo"
            element={
              <AuthGuard>
                <Todo />
              </AuthGuard>
            }
          />

          {/* Customer Job Rooms and Measurements Page - More specific route first */}
          <Route
            path="/customers/:id/jobs/:jobId/rooms"
            element={
              <AuthGuard>
                <ViewCustomerRooms />
              </AuthGuard>
            }
          />

          {/* Individual Customer Jobs Page with Highlighted Job */}
          <Route
            path="/customers/:customerId/jobs/:jobId"
            element={
              <AuthGuard>
                <CustomerJobs />
              </AuthGuard>
            }
          />

          {/* Individual Customer Jobs Page */}
          <Route
            path="/customers/:customerId/jobs"
            element={
              <AuthGuard>
                <CustomerJobs />
              </AuthGuard>
            }
          />

          {/* Driver Management Routes */}
          <Route
            path="/drivers"
            element={
              <AuthGuard>
                <Drivers />
              </AuthGuard>
            }
          />
          <Route
            path="/drivers/:driverId/queue"
            element={
              <AuthGuard>
                <DriverQueue />
              </AuthGuard>
            }
          />

          {/* Invoice Management Routes - Now using clean table design */}
          <Route
            path="/invoices"
            element={
              <AuthGuard>
                <Invoices />
              </AuthGuard>
            }
          />

          <Route
            path="/invoices/add"
            element={
              <AuthGuard>
                <AddInvoice />
              </AuthGuard>
            }
          />

          <Route
            path="/invoices/:id"
            element={
              <AuthGuard>
                <ViewInvoice />
              </AuthGuard>
            }
          />

          {/* Database Backup & Migration Utility - DEVELOPMENT ONLY */}
          {/* This route and component are completely excluded from production builds */}
          {import.meta.env.DEV && DatabaseBackup && (
            <Route
              path="/admin/backup"
              element={
                <AuthGuard>
                  <Suspense fallback={<div className="p-8 text-center">Loading backup utility...</div>}>
                    <DatabaseBackup />
                  </Suspense>
                </AuthGuard>
              }
            />
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
